import React, { useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { Avatar, Text, Group, Stack } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import moment from 'moment-timezone';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './AppointmentCalendar.css';
import { useAuth } from '../../../../context/AuthContext';
import { useCalendar } from '../../../../context/CalendarContext';
import { darkenColor, getLuminance } from '../../../../utils/colorUtils';
const localizer = momentLocalizer(moment);

const PALETTE_COLORS = [
  { 
    bg: '#6B46C1', 
    lightBg: 'rgba(107, 70, 193, 0.05)',
    text: '#FFFFFF' 
  },
  { 
    bg: '#4DB6AC', 
    lightBg: 'rgba(77, 182, 172, 0.05)', 
    text: '#FFFFFF' 
  },
  { 
    bg: '#FFB74D', 
    lightBg: 'rgba(255, 183, 77, 0.05)', 
    text: '#000000' 
  },
  { 
    bg: '#FF7F7F', 
    lightBg: 'rgba(255, 127, 127, 0.05)', 
    text: '#000000' 
  },
  { 
    bg: '#78909C', 
    lightBg: 'rgba(120, 144, 156, 0.05)', 
    text: '#FFFFFF' 
  },
  { 
    bg: '#37474F', 
    lightBg: 'rgba(55, 71, 79, 0.05)', 
    text: '#FFFFFF' 
  },
  { 
    bg: '#E6E6FA', 
    lightBg: 'rgba(230, 230, 250, 0.05)', 
    text: '#000000' 
  },
  { 
    bg: '#F5FFFA', 
    lightBg: 'rgba(245, 255, 250, 0.05)', 
    text: '#000000' 
  }
];

const formats = {
  dayFormat: (date, culture, localizer) => {
    const dayNumber = localizer.format(date, 'D', culture);
    const dayName = localizer.format(date, 'ddd', culture);
    return `${dayName} ${dayNumber}`;
  },
  eventTimeRangeStartFormat: (date, culture, localizer) => {
    return localizer.format(date, 'hh:mm A', culture) + ' >> ';
  }
};

function AppointmentCalendar({ events, onSelectEvent }) {
  const { filteredEmployees, selectedDate, setSelectedDate, selectedView, setSelectedView, dateRange } = useCalendar();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { currentUser } = useAuth();

  // Get current time and set minutes/seconds/milliseconds to 0 to start at current hour
  const scrollToTime = new Date();
  scrollToTime.setMinutes(0);
  scrollToTime.setSeconds(0);
  scrollToTime.setMilliseconds(0);

  const resources = filteredEmployees.map(employee => {
    const name = `${employee.first_name} ${employee.last_name}`;

    // Create an object with all working hours for each day
    const workingHours = {
      monday_start_time: employee.monday_start_time,
      monday_end_time: employee.monday_end_time,
      tuesday_start_time: employee.tuesday_start_time,
      tuesday_end_time: employee.tuesday_end_time,
      wednesday_start_time: employee.wednesday_start_time,
      wednesday_end_time: employee.wednesday_end_time,
      thursday_start_time: employee.thursday_start_time,
      thursday_end_time: employee.thursday_end_time,
      friday_start_time: employee.friday_start_time,
      friday_end_time: employee.friday_end_time,
      saturday_start_time: employee.saturday_start_time,
      saturday_end_time: employee.saturday_end_time,
      sunday_start_time: employee.sunday_start_time,
      sunday_end_time: employee.sunday_end_time,
    };

    return {
      id: employee.id,
      value: employee.id,
      title: employee.id === currentUser.userId ? `${name} (You)` : name,
      avatar: null,
      ...workingHours // Spread the working hours into the resource
    };
  });

  const ResourceHeader = ({ resource }) => (
    <Group position="center" gap={isMobile ? 4 : 'sm'} p={isMobile ? 0 : 'md'}>
      <Avatar size={isMobile ? 'sm' : 'md'} radius="xl" src={resource.avatar} color="violet">
        {resource.title[0]}
      </Avatar>
      <Text size="sm" weight={500}>
        {resource.title}
      </Text>
    </Group>
  );

  const EventComponent = ({ event }) => (
    <span>{event.title}</span>
  );

  // Custom toolbar component
  const CustomToolbar = (toolbar) => {
    const goToToday = () => {
      setSelectedDate(moment().toDate());
    };

    const goToBack = () => {
      setSelectedDate(moment(selectedDate).subtract(1, selectedView).toDate());
    };

    const goToNext = () => {
      setSelectedDate(moment(selectedDate).add(1, selectedView).toDate());
    };

    const dateRangeStart = dateRange.start;
    const dateRangeEnd = dateRange.end;
    const label = selectedView === 'week' ?
      `${moment(dateRangeStart).format('MMMM D, YYYY')} to ${moment(dateRangeEnd).subtract(1, 'day').format('MMMM D, YYYY')}` :
      moment(selectedDate).format('dddd, MMMM D, YYYY');

    return (
      <div style={{padding: '0 16px'}}>
        <div className="rbc-toolbar">
          <div className="rbc-btn-group">
            <button type="button" onClick={goToBack}>
              <IconChevronLeft size={18} />
            </button>
            <button type="button" onClick={goToToday}>TODAY</button>
            <button type="button" onClick={goToNext}>
              <IconChevronRight size={18} />
            </button>
          </div>
          <div hidden={isMobile}><span style={{fontSize: '1.1rem', fontWeight: '500'}}>{label}</span></div>
          <div className="rbc-btn-group">
            <button 
              type="button" 
              className={selectedView === 'day' ? 'rbc-active' : ''} 
              onClick={() => toolbar.onView('day')}
            >
              DAY
            </button>
            <button 
              type="button" 
              className={selectedView === 'week' ? 'rbc-active' : ''} 
              onClick={() => toolbar.onView('week')}
            >
              WEEK
            </button>
          </div>
        </div>
        <div hidden={!isMobile} style={{width: '100%', textAlign: 'center', paddingBottom: '16px'}}>
          <span style={{fontSize: '1.1rem', fontWeight: '500'}}>{toolbar.label}</span>
        </div>
      </div>
    );
  };

  // Add a custom header component
  const CustomHeader = ({ label, date }) => {
    const [dayName, dayNumber] = label.split(' ');
    const isToday = moment(date).isSame(moment(), 'day');
    
    return (
      <div 
        className={isToday ? 'rbc-header-today' : ''} 
      >
        {dayName} <span className={`day-number ${isToday ? 'today' : ''}`}>{dayNumber}</span>
      </div>
    );
  };

  const eventStyleGetter = (event) => {
    const backgroundColor = event.backgroundColor || '#3174ad';
    let className = '';
    
    const newStyle = {
      backgroundColor,
      borderColor: event.isTimeBlock ? '#686868' : darkenColor(backgroundColor),
      color: getLuminance(backgroundColor) > 0.6 ? '#25262b' : '#fff',
    };
    
    if (event.status === 'No Show') {
      className = 'no-show-pattern';
      delete newStyle.backgroundColor;
    } else if (event.status === 'Requested') {
      className = 'requested-pattern';
    }

    if (event.isTimeBlock) {
      newStyle.backgroundColor = "#747474";
      newStyle.borderColor = "#686868";
    }

    return {
      className,
      style: newStyle
    };
  };

  const slotPropGetter = (date, resourceId) => {
    const resource = resources.find(r => r.id === resourceId);
    if (!resource) return {};

    const currentDay = moment(date).format('dddd').toLowerCase();
    const slotTime = moment(date).format('HH:mm');
    
    // Get working hours for the current day
    const startTimeRaw = resource[`${currentDay}_start_time`];
    const endTimeRaw = resource[`${currentDay}_end_time`];

    // If no working hours set for this day, show as outside work hours
    if (!startTimeRaw || !endTimeRaw) {
      return { className: 'outside-work-hours' };
    }

    // Convert times to 24hr format for comparison
    const startTime = moment(startTimeRaw, 'h:mm A').format('HH:mm');
    const endTime = moment(endTimeRaw, 'h:mm A').format('HH:mm');

    // Check if the slot is within working hours
    const isWorkingHours = slotTime >= startTime && slotTime < endTime;

    return {
      className: isWorkingHours ? 'working-hours' : 'outside-work-hours',
    };
  };

  // // Custom week view component
  const CustomWeekView = () => {
    // Get color for calendar based on index
    const getCalendarStyle = (index) => {
      const colorIndex = index % PALETTE_COLORS.length;
      const color = PALETTE_COLORS[colorIndex];
      
      return {
        '--calendar-primary': color.bg,
        '--calendar-light': color.lightBg,
        '--calendar-text': color.text,
      };
    };

    return (
      <Group className="custom-week-view">
        {resources.map((resource, index) => (
          <div 
            key={resource.id} 
            className="artist-calendar"
            style={getCalendarStyle(index)}
          >
            <div className="artist-header">
              <Group position="center" gap={isMobile ? 4 : 'sm'}>
                <Avatar 
                  size={isMobile ? 'sm' : 'md'} 
                  radius="xl" 
                  color="white"
                  styles={{
                    placeholder: {
                      color: PALETTE_COLORS[index % PALETTE_COLORS.length].text,
                    },
                  }}
                >
                  {resource.title[0]}
                </Avatar>
                <Text 
                  size="sm" 
                  weight={500}
                  c={PALETTE_COLORS[index % PALETTE_COLORS.length].text}
                >
                  {resource.title}
                </Text>
              </Group>
            </div>
            <Calendar
              localizer={localizer}
              events={ events.filter(event => 
                event.resourceId === resource.id || 
                event.secondary_staff_id === resource.id
              )}
              startAccessor="start"
              endAccessor="end"
              defaultView="week"
              date={selectedDate}
              step={30}
              timeslots={2}
              selectable
              scrollToTime={scrollToTime}
              onSelectEvent={onSelectEvent}
              onNavigate={setSelectedDate}
              formats={formats}
              className="themed-calendar"
              components={{
                event: EventComponent,
                toolbar: () => null,
                header: (props) => (
                  <CustomHeader 
                    {...props} 
                  />
                ),
                timeGutterHeader: (props) => (
                  <div>
                    {props.children}
                  </div>
                ),
              }}
              dayLayoutAlgorithm="no-overlap"
              eventPropGetter={eventStyleGetter}
              slotPropGetter={(date) => slotPropGetter(date, resource.id)}
              showMultiDayTimes
            />
          </div>
        ))}
      </Group>
    );
  };

  return (
    <div className="appointment-calendar custom-calendar-toolbar">
      {selectedView === 'week' ? (
        <>
          <CustomToolbar 
              onView={setSelectedView}
              onNavigate={setSelectedDate}
              date={selectedDate}
              view={selectedView}
            />
          <CustomWeekView />
        </>
      ) : (
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          defaultView="day"
          date={selectedDate}
          step={30}
          timeslots={2}
          selectable
          scrollToTime={scrollToTime}
          onSelectEvent={onSelectEvent}
          onNavigate={setSelectedDate}
          onView={setSelectedView}
          resources={resources}
          resourceIdAccessor="id"
          resourceTitleAccessor="title"
          formats={formats}
          components={{
            resourceHeader: ResourceHeader,
            event: EventComponent,
            toolbar: CustomToolbar,
            header: CustomHeader
          }}
          views={['day', 'week']}
          dayLayoutAlgorithm="no-overlap"
          eventPropGetter={eventStyleGetter}
          slotPropGetter={slotPropGetter}
          showMultiDayTimes
        />
      )}
    </div>
  );
}

export default AppointmentCalendar; 